import { useMemo, useState } from 'react'
import { GestorJornadaMembroCard } from '@/components/Gestores/GestorJornadaMembroCard'
import { GestorJornadaMembrosTable } from '@/components/Gestores/GestorJornadaMembrosTable'
import { GestorListTableShell } from '@/components/Gestores/GestorListTableShell'
import {
  filterJornadaMembros,
  sortJornadaMembros,
  type JornadaSituacaoFilter,
  type JornadaViewMode,
} from '@/components/Gestores/gestorJornadaFilters'
import type { JornadaCadenciaDias } from '@/components/Gestores/gestorJornadaUi'
import type { GestorJornadaMembro } from '@/services/gestoresService'

type Props = {
  membros: GestorJornadaMembro[]
  search: string
  situacao: JornadaSituacaoFilter
  view: JornadaViewMode
  registrandoId: string | null
  onOpenFeedback: (memberId: string) => void
  onCadenciaChange: (pjId: string, cadencia: JornadaCadenciaDias) => Promise<void>
  onRegister: (pjId: string, body: { realizadaEm: string; notas?: string }) => Promise<void>
}

export function GestorJornadaMembrosPanel({
  membros,
  search,
  situacao,
  view,
  registrandoId,
  onOpenFeedback,
  onCadenciaChange,
  onRegister,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const filtered = useMemo(
    () => sortJornadaMembros(filterJornadaMembros(membros, { search, situacao })),
    [membros, search, situacao],
  )

  const toggleRegister = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  if (filtered.length === 0) {
    return (
      <GestorListTableShell
        empty
        emptyTitle='Nenhum profissional encontrado'
        emptyHint='Ajuste a busca ou os filtros de situação da 1:1'
      >
        <span />
      </GestorListTableShell>
    )
  }

  if (view === 'lista') {
    return (
      <GestorListTableShell>
        <div className='max-h-[min(620px,calc(100vh-22rem))] overflow-y-auto'>
          <GestorJornadaMembrosTable
            membros={filtered}
            expandedId={expandedId}
            registrandoId={registrandoId}
            onToggleRegister={toggleRegister}
            onOpenFeedback={onOpenFeedback}
            onCadenciaChange={onCadenciaChange}
            onRegister={onRegister}
          />
        </div>
      </GestorListTableShell>
    )
  }

  return (
    <div className='glass-card overflow-hidden p-0'>
      <ul className='max-h-[min(620px,calc(100vh-22rem))] space-y-2 overflow-y-auto p-3 sm:p-4'>
        {filtered.map((m) => (
          <GestorJornadaMembroCard
            key={m.id}
            membro={m}
            expanded={expandedId === m.id}
            registrando={registrandoId === m.id}
            onToggleRegister={() => toggleRegister(m.id)}
            onOpenFeedback={() => onOpenFeedback(m.id)}
            onCadenciaChange={(cadencia) => onCadenciaChange(m.id, cadencia)}
            onRegister={(body) => onRegister(m.id, body)}
          />
        ))}
      </ul>
    </div>
  )
}
